// Slice-internal Konva renderer for the line slice (PRD-F5 §TR5).
//
// Logic extracted from `objects/board-object-renderer.ts:createLineNode` /
// `updateLineNode` (the legacy branch is removed in T24). The path geometry
// is delegated to `LinePathStrategy` via the per-element
// `LinePathStrategyRegistry` — the renderer never hard-codes a strategy.

import Konva from 'konva';
import type { SliceRenderContext } from '../../core/object-registry.js';
import type { LineObject, LineCap } from './line.interface.js';
import { konvaDashPattern, konvaDropShadowAttrs } from '../../objects/konva-decoration.helper.js';
import { LINE_HIT_STROKE_PX } from '../../constants/whiteboard-config.constants.js';
import type {
  KonvaLineProps,
  LinePathStrategyRegistry,
} from './strategies/line-path-strategy.interface.js';

const LINE_STROKE_NAME = 'line-stroke';
const LINE_START_CAP_NAME = 'line-start-cap';
const LINE_END_CAP_NAME = 'line-end-cap';

/** Arrow half-width / length in world pixels at strokeWidth 1. Scales with strokeWidth. */
const ARROW_BASE = 6;
const ARROW_HEIGHT = 12;
/** Dot radius at strokeWidth 1. */
const DOT_RADIUS = 4;
/** Square half-size at strokeWidth 1. */
const SQUARE_HALF = 4;

/**
 * Renderer pair for the line slice. Produced by `createLineRenderer` so the
 * `LinePathStrategyRegistry` can be injected via closure — keeps the
 * `SlicePlugin.createNode/updateNode` signatures untouched.
 */
export interface LineRenderer {
  createNode(obj: LineObject, ctx: SliceRenderContext): Konva.Group;
  updateNode(node: Konva.Node, obj: LineObject, ctx: SliceRenderContext): void;
}

/**
 * Dash + drop-shadow attrs for the stroke, from the shared decoration helper
 * (same source as rectangle / ellipse, so the options behave identically).
 */
function strokePaintAttrs(obj: LineObject): Konva.ShapeConfig {
  return {
    dash: konvaDashPattern(obj.strokeStyle, obj.strokeWidth),
    ...konvaDropShadowAttrs(obj.boxShadow === 'on'),
  };
}

/**
 * Build a `LineRenderer` bound to the given strategy registry. Called once
 * per element from `lineSlice` (TR8b / T26) after the registry has been
 * populated with `StraightPathStrategy` (T25).
 */
export function createLineRenderer(registry: LinePathStrategyRegistry): LineRenderer {
  return {
    createNode(obj: LineObject): Konva.Group {
      const group = new Konva.Group({
        id: obj.id,
        x: obj.x,
        y: obj.y,
        width: obj.width,
        height: obj.height,
        rotation: obj.rotation,
        name: 'line-obj',
      });

      const props = computeLineProps(obj, registry);
      const stroke = new Konva.Line({
        name: LINE_STROKE_NAME,
        points: [...props.points],
        stroke: obj.strokeColor,
        strokeWidth: obj.strokeWidth,
        lineCap: 'round',
        lineJoin: 'round',
        hitStrokeWidth: Math.max(LINE_HIT_STROKE_PX, obj.strokeWidth),
        ...(props.tension !== undefined ? { tension: props.tension } : {}),
        ...(props.bezier !== undefined ? { bezier: props.bezier } : {}),
        ...strokePaintAttrs(obj),
      });
      group.add(stroke);

      const { start, end } = capAnchors(props.points, props.tension);
      const startCap = buildCapShape(obj.startCap, start.at[0], start.at[1], start.from[0], start.from[1], obj.strokeColor, obj.strokeWidth, LINE_START_CAP_NAME);
      const endCap = buildCapShape(obj.endCap, end.at[0], end.at[1], end.from[0], end.from[1], obj.strokeColor, obj.strokeWidth, LINE_END_CAP_NAME);
      if (startCap) group.add(startCap);
      if (endCap) group.add(endCap);
      applyCapShadow(startCap, endCap, obj);

      return group;
    },

    updateNode(node: Konva.Node, obj: LineObject): void {
      if (!(node instanceof Konva.Group)) return;
      // Base geometry (group x/y/width/height/rotation) is applied centrally
      // by `applyBaseGeometry`; this method only recomputes the stroke path
      // and end caps in the group's local frame.
      const props = computeLineProps(obj, registry);
      const stroke = node.findOne<Konva.Line>(`.${LINE_STROKE_NAME}`);
      if (stroke) {
        stroke.points([...props.points]);
        stroke.stroke(obj.strokeColor);
        stroke.strokeWidth(obj.strokeWidth);
        stroke.hitStrokeWidth(Math.max(LINE_HIT_STROKE_PX, obj.strokeWidth));
        // Always written, falling back to Konva's own defaults: a strategy that
        // returns no tension/bezier (straight, orthogonal) must reset what a
        // previous strategy (curved) left on the reused node — otherwise a
        // bezier line switched to right-angled kept being drawn as a curve
        // (Anke, 2026-10-03).
        stroke.tension(props.tension ?? 0);
        stroke.bezier(props.bezier ?? false);
        // setAttrs (not individual setters) so switching back to `solid` /
        // shadow-off resets every attr the helper owns.
        stroke.setAttrs(strokePaintAttrs(obj));
      }

      // Caps are rebuilt rather than mutated — keeps the shape-type swap
      // (e.g. arrow → dot) trivial without per-cap-type branches.
      node.findOne(`.${LINE_START_CAP_NAME}`)?.destroy();
      node.findOne(`.${LINE_END_CAP_NAME}`)?.destroy();
      const { start, end } = capAnchors(props.points, props.tension);
      const startCap = buildCapShape(obj.startCap, start.at[0], start.at[1], start.from[0], start.from[1], obj.strokeColor, obj.strokeWidth, LINE_START_CAP_NAME);
      const endCap = buildCapShape(obj.endCap, end.at[0], end.at[1], end.from[0], end.from[1], obj.strokeColor, obj.strokeWidth, LINE_END_CAP_NAME);
      if (startCap) node.add(startCap);
      if (endCap) node.add(endCap);
      applyCapShadow(startCap, endCap, obj);
    },
  };
}

/**
 * Give the caps the same drop shadow as the stroke — but never the dash
 * pattern: a dashed arrow head would fall apart into fragments.
 */
function applyCapShadow(
  startCap: Konva.Shape | null,
  endCap: Konva.Shape | null,
  obj: LineObject,
): void {
  const shadow = konvaDropShadowAttrs(obj.boxShadow === 'on');
  startCap?.setAttrs(shadow);
  endCap?.setAttrs(shadow);
}

/**
 * Translate the world-coordinate endpoints into the Group's local frame.
 * The Group origin sits at the bbox top-left, so locals are simply deltas.
 */
function endpointsLocal(obj: LineObject): readonly [number, number, number, number] {
  return [obj.x1 - obj.x, obj.y1 - obj.y, obj.x2 - obj.x, obj.y2 - obj.y];
}

/**
 * Auch vom SVG-Export genutzt (core/export): Er muss dieselbe Geometrie
 * zeichnen wie die Anzeige — inklusive Kurven-Spannung.
 */
export function computeLineProps(obj: LineObject, registry: LinePathStrategyRegistry): KonvaLineProps {
  const [lx1, ly1, lx2, ly2] = endpointsLocal(obj);
  const strategy = registry.get(obj.pathStrategy);
  // Forward the docked edge sides so an orthogonal strategy can keep the
  // touching segments perpendicular to the edge they attach to, and the
  // middle-segment ratio the user dragged. Free-form strategies (straight)
  // ignore the hints.
  // The curved strategy reads its control points (translated into the local
  // frame) from `controlPoints`; other strategies ignore them.
  const localControls = obj.curveControls?.length
    ? obj.curveControls.map((c) => ({ x: c.x - obj.x, y: c.y - obj.y }))
    : undefined;
  return strategy.computePath({ x: lx1, y: ly1 }, { x: lx2, y: ly2 }, localControls, {
    startSide: obj.startAnchor?.side,
    endSide: obj.endAnchor?.side,
    midRatio: obj.midRatio,
  });
}

/**
 * The line's polyline in WORLD coordinates: the strategy-computed local points
 * translated by the group origin (obj.x, obj.y). Shared with
 * `LineMidSegmentHandleController` so the drag handle sits on exactly the
 * geometry the renderer draws. Lines carry rotation 0, so local + origin = world.
 */
export function computeLineWorldPoints(
  obj: LineObject,
  registry: LinePathStrategyRegistry,
): number[] {
  const { points } = computeLineProps(obj, registry);
  const world: number[] = [];
  for (let i = 0; i < points.length; i += 2) {
    world.push(points[i]! + obj.x, points[i + 1]! + obj.y);
  }
  return world;
}

/**
 * Konva's cardinal-spline control-point formula (mirrors `getControlPoints` in
 * konva/Line): the pair of Bézier control points around the middle point
 * `(x1,y1)` for tension `t`. Returns `null` for coincident points (which would
 * divide by zero).
 */
export function splineControlPoints(
  x0: number, y0: number, x1: number, y1: number, x2: number, y2: number, t: number,
): { in: [number, number]; out: [number, number] } | null {
  const d01 = Math.hypot(x1 - x0, y1 - y0);
  const d12 = Math.hypot(x2 - x1, y2 - y1);
  if (d01 + d12 === 0) return null;
  const fa = (t * d01) / (d01 + d12);
  const fb = (t * d12) / (d01 + d12);
  return {
    in: [x1 - fa * (x2 - x0), y1 - fa * (y2 - y0)],
    out: [x1 + fb * (x2 - x0), y1 + fb * (y2 - y0)],
  };
}

/**
 * Where each cap sits and which neighbour orients it.
 *
 * For a straight/orthogonal polyline the orienting neighbour is the adjacent
 * vertex, so the arrow follows the first/last segment. For a `tension` curve
 * that vertex is the wrong direction — Konva renders the end segments as
 * quadratic Béziers, so the true tangent at an endpoint points from the spline
 * CONTROL point (not the polyline point) to the endpoint. Using that control
 * point keeps the arrow aligned with the curve instead of twisted.
 */
function capAnchors(points: readonly number[], tension?: number): {
  start: { at: readonly [number, number]; from: readonly [number, number] };
  end: { at: readonly [number, number]; from: readonly [number, number] };
} {
  const n = points.length;
  const startAt: [number, number] = [points[0]!, points[1]!];
  const endAt: [number, number] = [points[n - 2]!, points[n - 1]!];
  let startFrom: [number, number] = [points[2]!, points[3]!];
  let endFrom: [number, number] = [points[n - 4]!, points[n - 3]!];

  if (tension && tension !== 0 && n >= 6) {
    const s = splineControlPoints(points[0]!, points[1]!, points[2]!, points[3]!, points[4]!, points[5]!, tension);
    const e = splineControlPoints(points[n - 6]!, points[n - 5]!, points[n - 4]!, points[n - 3]!, points[n - 2]!, points[n - 1]!, tension);
    if (s) startFrom = s.in;
    if (e) endFrom = e.out;
  }

  return {
    start: { at: startAt, from: startFrom },
    end: { at: endAt, from: endFrom },
  };
}

/**
 * Build one cap shape positioned at (px,py), pointing away from (ox,oy).
 * Returns null for `cap === 'none'` so the caller can simply skip it.
 */
function buildCapShape(
  cap: LineCap,
  px: number,
  py: number,
  ox: number,
  oy: number,
  color: string,
  sw: number,
  name: string,
): Konva.Shape | null {
  if (cap === 'none') return null;
  const angle = Math.atan2(py - oy, px - ox);
  const scale = Math.max(1, sw);

  if (cap === 'arrow') {
    const h = ARROW_HEIGHT * scale;
    const hw = ARROW_BASE * scale;
    const bx = px - Math.cos(angle) * h;
    const by = py - Math.sin(angle) * h;
    const p1x = bx - Math.cos(angle + Math.PI / 2) * hw;
    const p1y = by - Math.sin(angle + Math.PI / 2) * hw;
    const p2x = bx + Math.cos(angle + Math.PI / 2) * hw;
    const p2y = by + Math.sin(angle + Math.PI / 2) * hw;
    return new Konva.Line({
      name,
      points: [p1x, p1y, px, py, p2x, p2y],
      closed: true,
      fill: color,
      stroke: color,
      strokeWidth: 0.5,
      listening: false,
    });
  }

  if (cap === 'dot') {
    const r = DOT_RADIUS * scale;
    return new Konva.Circle({
      name,
      x: px,
      y: py,
      radius: r,
      fill: color,
      listening: false,
    });
  }

  if (cap === 'square') {
    const half = SQUARE_HALF * scale;
    return new Konva.Rect({
      name,
      x: px,
      y: py,
      width: half * 2,
      height: half * 2,
      offsetX: half,
      offsetY: half,
      fill: color,
      rotation: (angle * 180) / Math.PI + 45,
      listening: false,
    });
  }

  return null;
}

// Internal names exposed for tests + the LineEndpointHandlesController (T20+).
export const LINE_NODE_NAMES = {
  STROKE: LINE_STROKE_NAME,
  START_CAP: LINE_START_CAP_NAME,
  END_CAP: LINE_END_CAP_NAME,
} as const;
